/**
 * DataTable — Cohere research-table style primitive.
 *
 * Implements the no-live-re-sort invariant: an internal `displayedKeys` array
 * preserves existing row order across reactive updates. New rows append at the
 * bottom; removed rows are pruned; existing rows never re-shuffle unless the
 * consumer explicitly resets via a user-driven sort or filter.
 *
 * @see specs/integration-plan.md Section 3.4
 * @see specs/prd.md FR-003 (7 foundational primitives)
 */
import React, { useRef, useMemo } from "react";
import { default as Card } from "./Card";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface DataTableColumn<Row> {
  key: keyof Row & string;
  header: string;
  width?: string | number;
  render?: (value: Row[keyof Row], row: Row) => React.ReactNode;
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  emptyState?: React.ReactNode;
  isLoading?: boolean;
  appendOnly?: boolean; // defaults to true
  /**
   * Responsive rendering mode (FR-008 — T306).
   *   - "card-stack": renders an alternate card-stack DOM variant below 768px
   *     where each row becomes a Card with column labels as mono-label captions.
   *     CSS class display switching selects the visible variant per band.
   *   - "none": standard table at all viewport widths.
   * Default: "card-stack".
   */
  responsive?: "none" | "card-stack";
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SKELETON_COUNT = 6;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  emptyState,
  isLoading = false,
  responsive = "card-stack",
}: DataTableProps<Row>) {
  // -----------------------------------------------------------------------
  // No-live-re-sort invariant
  //
  // `displayedKeysRef` holds the stable key ordering. On each render:
  //   1. Existing keys that are still present retain their positions.
  //   2. New keys are appended at the bottom.
  //   3. Removed keys are pruned.
  // INVARIANT: no live re-sort — existing rows never re-shuffle.
  // -----------------------------------------------------------------------
  const displayedKeysRef = useRef<string[]>([]);

  const orderedRows = useMemo(() => {
    const incomingMap = new Map<string, Row>();
    for (const row of rows) {
      incomingMap.set(rowKey(row), row);
    }

    const incomingKeys = new Set(incomingMap.keys());
    const prevKeys = displayedKeysRef.current;

    // Keep existing keys that are still present (preserving order)
    const stableKeys: string[] = [];
    for (const k of prevKeys) {
      if (incomingKeys.has(k)) {
        stableKeys.push(k);
      }
    }

    // Append new keys not in the previous set
    const existingSet = new Set(prevKeys);
    for (const k of incomingKeys) {
      if (!existingSet.has(k)) {
        stableKeys.push(k);
      }
    }

    // Persist for next render
    displayedKeysRef.current = stableKeys;

    // Build the ordered row array
    const result: Row[] = [];
    for (const k of stableKeys) {
      const row = incomingMap.get(k);
      if (row !== undefined) {
        result.push(row);
      }
    }
    return result;
  }, [rows, rowKey]);

  // -----------------------------------------------------------------------
  // Styles — CSS custom-property references only
  // -----------------------------------------------------------------------

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "var(--font-ui)",
    fontSize: "var(--fs-body-lg)",
    lineHeight: 1.4,
    backgroundColor: "var(--bg1)",
  };

  const thStyle: React.CSSProperties = {
    textTransform: "uppercase",
    fontSize: "var(--fs-mono)",
    fontFamily: "var(--font-ui)",
    fontWeight: 500,
    color: "var(--fg3)",
    textAlign: "left",
    padding: "var(--space-lg) var(--space-md)",
    borderBottom: "1px solid var(--bg2)",
    verticalAlign: "top",
  };

  const tdStyle: React.CSSProperties = {
    padding: "var(--space-lg) var(--space-md)",
    borderBottom: "1px solid var(--bg2)",
    verticalAlign: "top",
    fontFamily: "var(--font-ui)",
    fontSize: "var(--fs-body-lg)",
    color: "var(--fg1)",
  };

  // -----------------------------------------------------------------------
  // Loading state — 6 skeleton placeholders
  // -----------------------------------------------------------------------

  if (isLoading) {
    return (
      <table style={tableStyle} role="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{
                  ...thStyle,
                  ...(col.width != null
                    ? { width: typeof col.width === "number" ? `${col.width}px` : col.width }
                    : {}),
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <tr key={i} data-testid="skeleton-row">
              {columns.map((col) => (
                <td key={col.key} style={tdStyle}>
                  <div
                    style={{
                      height: "1em",
                      borderRadius: "var(--radius-xs)",
                      backgroundColor: "var(--bg2)",
                    }}
                    aria-hidden="true"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // -----------------------------------------------------------------------
  // Empty state
  // -----------------------------------------------------------------------

  if (rows.length === 0 && emptyState) {
    return (
      <table style={tableStyle} role="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                style={{
                  ...thStyle,
                  ...(col.width != null
                    ? { width: typeof col.width === "number" ? `${col.width}px` : col.width }
                    : {}),
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* empty — no rows */}
        </tbody>
        {emptyState}
      </table>
    );
  }

  // -----------------------------------------------------------------------
  // Card-stack mono-label caption style (FR-008 — T306)
  // -----------------------------------------------------------------------

  const monoLabelStyle: React.CSSProperties = {
    fontFamily: "var(--font-mono, monospace)",
    fontSize: "var(--fs-micro, 12px)",
    color: "var(--fg3, #75758a)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "var(--space-xxs, 2px)",
  };

  // -----------------------------------------------------------------------
  // Populated state
  // -----------------------------------------------------------------------

  const tableElement = (
    <table style={tableStyle} role="table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              scope="col"
              style={{
                ...thStyle,
                ...(col.width != null
                  ? { width: typeof col.width === "number" ? `${col.width}px` : col.width }
                  : {}),
              }}
            >
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {orderedRows.map((row) => {
          const key = rowKey(row);
          return (
            <tr
              key={key}
              style={{
                cursor: onRowClick ? "pointer" : undefined,
              }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => {
                const value = row[col.key];
                return (
                  <td key={col.key} style={tdStyle}>
                    {col.render ? col.render(value, row) : String(value)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // No responsive variant requested — render table only
  if (responsive === "none") {
    return tableElement;
  }

  // Responsive "card-stack" mode: render both variants; CSS switches visibility
  return (
    <>
      {/* Standard table (visible >=768px via responsive.css) */}
      <div className="datatable__table">
        {tableElement}
      </div>

      {/* Card-stack variant (visible <768px via responsive.css) */}
      <div className="datatable__card-stack" data-testid="datatable-card-stack">
        {orderedRows.map((row) => {
          const key = rowKey(row);
          return (
            <Card
              key={key}
              depth="bordered"
              padding="xl"
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{ cursor: onRowClick ? "pointer" : undefined }}
            >
              {columns.map((col) => {
                const value = row[col.key];
                return (
                  <div key={col.key} style={{ marginBottom: "var(--space-sm, 8px)" }}>
                    <div style={monoLabelStyle}>{col.header}</div>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: "var(--fs-body, 16px)", color: "var(--fg1, #212121)" }}>
                      {col.render ? col.render(value, row) : String(value)}
                    </div>
                  </div>
                );
              })}
            </Card>
          );
        })}
      </div>
    </>
  );
}
